import { ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import validate from './form/validate';
import Button from '~/ui/components/common/Button';
import Checkbox from '~/ui/components/inputs/Checkbox';
import DatePicker from '~/ui/components/inputs/DatePicker';

import { IFormValues } from './types';
import styles from './NoteForm.module.scss';
import { TemplateType } from '~/services/api/enums';
import UseTemplateButton from '../../UseTemplateButton';
import Input from '~/ui/components/inputs/Input';
import SelectVirtualized from '~/ui/components/inputs/SelectVirtualized';
import { noteMarkTypeOptions } from './constants';
import NoteTextEditor from '../NoteTextEditor';

interface IProps {
  mode?: 'create' | 'edit';
  showBackBtn?: boolean;
  initialValues: IFormValues;
  isProcessing: boolean;
  onSubmit: (data: IFormValues, reset: any) => void;
  onClose?: () => void;
}

const NoteForm = ({
  mode = 'create',
  showBackBtn = false,
  initialValues,
  isProcessing,
  onSubmit,
  onClose,
}: IProps): ReactElement => {
  const {
    register,
    control,
    watch,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: validate,
    defaultValues: initialValues,
  });

  const showReminder = watch('showReminder');
  const isSentForTriage = watch('isSentForTriage');

  const onTemplateSelected = (html: string) => {
    setValue('contentHtml', html, { shouldDirty: true, shouldValidate: true, shouldTouch: true });
  };

  return (
    <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Grid item xs={4}>
              <Checkbox
                name="isTelephoneConversation"
                control={control}
                errors={errors}
                label="Telephone Conversation"
              />
            </Grid>
            <Grid item xs={4}>
              <Checkbox
                name="showReminder"
                control={control}
                errors={errors}
                label="Reminder to follow up"
              />
            </Grid>
            <Grid item xs={4}>
              {showReminder && (
                <DatePicker
                  name="reminderDate"
                  minDate={new Date()}
                  control={control}
                  errors={errors}
                  label="Date"
                  required
                />
              )}
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Grid item xs={4}>
              <Checkbox
                name="notifyHealthManager"
                control={control}
                errors={errors}
                label="Notify Health Manager"
              />
            </Grid>
            <Grid item xs={4}>
              <Checkbox
                name="isSentForTriage"
                control={control}
                errors={errors}
                label="Send For Triage"
              />
            </Grid>
            <Grid item xs={4}>
              {isSentForTriage && (
                <Input
                  name="issueName"
                  register={register}
                  errors={errors}
                  label="Issue Name"
                  required
                />
              )}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <div className={styles.heightSpacer} />

      <NoteTextEditor
        name="content"
        placeholder="Write a note..."
        control={control}
        setValue={setValue}
      />

      <Grid container className={styles.btnWrapper} alignItems="center" spacing={2}>
        {showBackBtn && (
          <Grid item>
            <Button
              variant="outlined"
              color="default"
              startIcon={<ArrowBackIosIcon />}
              onClick={onClose}
            >
              Back
            </Button>
            <div className={styles.spacer} />
          </Grid>
        )}
        <Grid item>
          <UseTemplateButton typeId={TemplateType.Notes} onTemplateSelected={onTemplateSelected} />
        </Grid>
        <Grid item xs={4}>
          <SelectVirtualized
            name="markTypeId"
            control={control}
            errors={errors}
            options={noteMarkTypeOptions}
            label="Mark Note"
            menuPlacement="top"
          />
        </Grid>
        <Grid item xs />
        {isSentForTriage && (
          <Grid item>
            <Checkbox
              name="isHighPriority"
              control={control}
              errors={errors}
              label="High Priority"
            />
          </Grid>
        )}
        <Grid item>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            isLoading={isProcessing}
            disabled={isProcessing}
          >
            {mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default NoteForm;
